import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Grid,
  TextField,
  Box,
  FormHelperText,
  Chip,
  IconButton,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Typography from "@mui/material/Typography";
import EditIcon from "@mui/icons-material/Edit";
import "../../../styles.css";
import TooltipComponent from "../../common/Tooltip";
import { useTheme } from "@mui/material/styles";
import { createFilterOptions } from "@mui/material/Autocomplete";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";

function EditableMultiSelectQuestionComponent({ question }) {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const theme = useTheme();
  const currentValues = watch(question.questionName) || [];
  const [selectedOptions, setSelectedOptions] = useState([]); // Items shown as chips
  const [allItems, setAllItems] = useState([]); // All items for dropdown list
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    // Initialize selected options and all items from form values
    if (Array.isArray(currentValues) && currentValues.length > 0) {
      setSelectedOptions(currentValues);
      setAllItems(currentValues); // Initially, all items = selected items
    }
  }, []);

  const filter = createFilterOptions({
    stringify: (option) => option,
  });

  const openEditDialog = (index, value) => {
    setEditingIndex(index);
    setEditingValue(value);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (editingValue.trim() && editingIndex !== null) {
      const oldValue = allItems[editingIndex];
      const newValue = editingValue.trim();
      
      // Update allItems
      const updatedAllItems = [...allItems];
      updatedAllItems[editingIndex] = newValue;
      setAllItems(updatedAllItems);
      
      // Update selectedOptions if the old value was selected
      if (selectedOptions.includes(oldValue)) {
        const updatedSelected = selectedOptions.map(item => 
          item === oldValue ? newValue : item
        );
        setSelectedOptions(updatedSelected);
        setValue(question.questionName, updatedSelected);
      }
    }
    setEditDialogOpen(false);
    setEditingIndex(null);
    setEditingValue("");
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingIndex(null);
    setEditingValue("");
  };

  return (
    <Grid
      item
      xs={12}
      sx={{
        mb: 2,
        ml: question?.isSuggested ? 2 : 0,
        backgroundColor: question?.isSuggested
          ? theme.palette.primary.nestedQuestionBackground
          : "",
        py: question?.isSuggested ? 2 : 0,
        px: question?.isSuggested ? 1.5 : 0,
        borderRadius: question?.isSuggested ? 2 : 0,
        border: question?.isSuggested ? "1px solid #fff" : "none",
        boxShadow: question?.isSuggested ? "0px 2px 4px rgba(0, 0, 0, 0.2)" : 0,
        cursor: question?.isSuggested ? "pointer" : "",
      }}
    >
      <Typography
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        className="stepper-question"
      >
        <Box>
          {question.label}
          {question.rules.required && <span style={{ color: "red" }}>*</span>}
        </Box>
        {question.question_info?.trim() && (
          <TooltipComponent message={question.question_info} />
        )}
      </Typography>

      <Controller
        name={question.questionName}
        control={control}
        rules={question.rules}
        render={({ field }) => (
          <Autocomplete
            multiple
            freeSolo
            disableCloseOnSelect
            {...field}
            value={selectedOptions}
            onChange={(event, newValue) => {
              // Process new values: trim and reject blank/whitespace-only entries
              const processedValues = newValue
                .map((value) => {
                  if (typeof value === "string") {
                    return value.trim();
                  }
                  if (value?.inputValue) {
                    return value.inputValue.trim();
                  }
                  return value;
                })
                .filter((val) => val !== ""); // Prevent blank chips (e.g. user typed only spaces + Enter)

              setSelectedOptions(processedValues);
              field.onChange(processedValues);

              const newItems = processedValues.filter((item) => !allItems.includes(item));
              if (newItems.length > 0) {
                setAllItems([...allItems, ...newItems]);
              }
            }}
            options={allItems}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);
              const { inputValue } = params;
              const trimmedInput = inputValue.trim();

              // Check if the input exists in options
              const isExisting = options.some(
                (option) =>
                  option.toLowerCase() === trimmedInput.toLowerCase()
              );

              // Add "Add option" if input is not empty and doesn't exist
              if (trimmedInput !== "" && !isExisting) {
                filtered.push({
                  inputValue: trimmedInput,
                  label: `Add "${trimmedInput}" option`,
                });
              }

              return filtered;
            }}
            getOptionLabel={(option) => {
              if (typeof option === "string") {
                return option;
              }
              if (option.inputValue) {
                return option.inputValue;
              }
              return option.label || option;
            }}
            renderOption={(props, option, { selected }) => {
              const optionIndex = allItems.indexOf(option);
              const isAddOption = option.inputValue !== undefined;

              // If this is the "Add option"
              if (isAddOption) {
                const matches = match(option.label, option.inputValue);
                const parts = parse(option.label, matches);
                
                return (
                  <li {...props} key={`add-${option.inputValue}`}>
                    <div>
                      {parts.map((part, index) => (
                        <span
                          key={index}
                          style={{
                            color: part.highlight ? "red" : "inherit",
                            fontWeight: part.highlight ? 700 : 400,
                          }}
                        >
                          {part.text}
                        </span>
                      ))}
                    </div>
                  </li>
                );
              }

              // Regular option with checkbox and edit icon
              return (
                <li
                  {...props}
                  key={optionIndex}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                    }}
                  >
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8, flexShrink: 0 }}
                      checked={selected}
                    />
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={option}
                    >
                      {option}
                    </span>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditDialog(optionIndex, option);
                    }}
                    sx={{ ml: 1, flexShrink: 0 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </li>
              );
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={index}
                  label={option}
                  {...getTagProps({ index })}
                  sx={{
                    backgroundColor: "#f1f1f1",
                    color: "#000",
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                autoFocus={question?.autoFocus}
                {...params}
                label={question.placeholder}
              />
            )}
          />
        )}
      />

      {errors[question.questionName]?.type === "required" && (
        <FormHelperText error className={"errorMsg"}>
          {question.errorMsg}
        </FormHelperText>
      )}

      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleCancelEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Item</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Value"
            type="text"
            fullWidth
            variant="outlined"
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSaveEdit();
              }
            }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default EditableMultiSelectQuestionComponent;
